import React, { useState, useRef, useEffect } from 'react';
import {
    Card,
    Box,
    Tabs,
    Tab,
    TextField,
    Button,
    IconButton,
    Typography,
    InputAdornment,
    ClickAwayListener,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';

interface ExportCardProps {
    visible: boolean;
    topPosition: number;
    leftPosition: number;
    handleClose: () => void;
    onExport: (type: 'video' | 'audio' | 'transcript', options: ExportOptions) => void;
}

export interface ExportOptions {
    name: string;
    resolution?: string;
    format?: string;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`export-tabpanel-${index}`}
            aria-labelledby={`export-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

export const ExportCard: React.FC<ExportCardProps> = ({ 
    visible,
    handleClose, 
    onExport 
}) => {
    const [tabValue, setTabValue] = useState(0);
    const [videoName, setVideoName] = useState('');
    const [audioName, setAudioName] = useState('');
    const [transcriptName, setTranscriptName] = useState('');
    const [resolution, setResolution] = useState('720p');
    const [audioFormat, setAudioFormat] = useState('Mp3');
    const [transcriptFormat, setTranscriptFormat] = useState('SRT');
    const cardRef = useRef<HTMLDivElement>(null);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleVideoExport = () => {
        onExport('video', {
            name: videoName || 'Untitled Video',
            resolution: resolution,
        });
        handleClose();
    };

    const handleAudioExport = () => {
        onExport('audio', {
            name: audioName || 'Untitled Audio',
            format: audioFormat,
        });
        handleClose();
    };

    const handleTranscriptExport = () => {
        onExport('transcript', {
            name: transcriptName || 'Untitled Transcript',
            format: transcriptFormat,
        });
        handleClose();
    };

    if (!visible) return null;

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <Card
                ref={cardRef}
                sx={{
                    position: 'absolute',
                    top: "55px",
                    right: "10px",
                    width: '360px',
                    borderRadius: '12px',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                    zIndex: 1300,
                    backgroundColor: 'white',
                }}
            >
                {/* Header */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        px: 2,
                        pt: 2,
                        pb: 1,
                    }}
                >
                    <Typography component="div" sx={{ fontSize: '18px', fontWeight: 600 }}>
                        Export
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={handleClose}
                        sx={{ p: 0.5 }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        sx={{
                            px: 2,
                            minHeight: '40px',
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontSize: '14px',
                                fontWeight: 500,
                                minHeight: '40px',
                                color: '#666',
                                '&.Mui-selected': {
                                    color: '#000',
                                }
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#2B6BFD',
                            }
                        }}
                    >
                        <Tab label="Video" />
                        <Tab label="Audio" />
                        <Tab label="Transcript" />
                    </Tabs>
                </Box>

                {/* Content */}
                <Box sx={{ px: 2, py: 2 }}>
                    <TabPanel value={tabValue} index={0}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Video Name Input */}
                            <TextField
                                fullWidth
                                value={videoName}
                                onChange={(e) => setVideoName(e.target.value)}
                                placeholder="Enter filename"
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <VideoFileIcon sx={{ color: '#999', fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: '14px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '8px',
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#e0e0e0',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#2B6BFD',
                                            borderWidth: '1px',
                                        }
                                    }
                                }}
                            />

                            {/* Resolution Dropdown */}
                            <Box>
                                <Typography 
                                    variant="body2" 
                                    sx={{ mb: 0.5, fontWeight: 500, fontSize: '13px' }}
                                >
                                    Resolution
                                </Typography>
                                <ToggleButtonGroup
                                    value={resolution}
                                    exclusive
                                    onChange={(event, newResolution) => {
                                        if (newResolution !== null) {
                                            setResolution(newResolution);
                                        }
                                    }}
                                    aria-label="resolution"
                                    size="small"
                                    fullWidth
                                    sx={{
                                        '& .MuiToggleButton-root': {
                                            fontSize: '13px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            backgroundColor: '#f8f9fa',
                                            color: '#666',
                                            border: '1px solid #e0e0e0',
                                            '&:hover': {
                                                backgroundColor: '#f0f0f0',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#2B6BFD',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: '#1e5ae5',
                                                },
                                            },
                                            '&:first-of-type': {
                                                borderTopLeftRadius: '8px',
                                                borderBottomLeftRadius: '8px',
                                            },
                                            '&:last-of-type': {
                                                borderTopRightRadius: '8px',
                                                borderBottomRightRadius: '8px',
                                            },
                                        },
                                        '& .MuiToggleButtonGroup-grouped': {
                                            '&:not(:last-of-type)': {
                                                borderRight: 0,
                                            },
                                        },
                                    }}
                                >
                                    <ToggleButton value="480p">480p</ToggleButton>
                                    <ToggleButton value="720p">720p</ToggleButton>
                                    <ToggleButton value="1080p">1080p</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {/* Export Button */}
                            <Button
                                variant="contained"
                                onClick={handleVideoExport}
                                fullWidth
                                sx={{
                                    mt: 1,
                                    height: '40px',
                                    textTransform: 'none',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    backgroundColor: '#2B6BFD',
                                    borderRadius: '8px',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#1e5ae5',
                                        boxShadow: 'none',
                                    }
                                }}
                            >
                                Export
                            </Button>
                        </Box>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Audio Name Input */}
                            <TextField
                                fullWidth
                                value={audioName}
                                onChange={(e) => setAudioName(e.target.value)}
                                placeholder="Enter filename"
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <AudioFileIcon sx={{ color: '#999', fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: '14px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '8px',
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#e0e0e0',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#2B6BFD',
                                            borderWidth: '1px',
                                        }
                                    }
                                }}
                            />

                            {/* Audio Format Dropdown */}
                            <Box>
                                <Typography 
                                    variant="body2" 
                                    sx={{ mb: 0.5, fontWeight: 500, fontSize: '13px' }}
                                >
                                    Format
                                </Typography>
                                <ToggleButtonGroup
                                    value={audioFormat}
                                    exclusive
                                    onChange={(event, newFormat) => {
                                        if (newFormat !== null) {
                                            setAudioFormat(newFormat);
                                        }
                                    }}
                                    aria-label="audio format"
                                    size="small"
                                    fullWidth
                                    sx={{
                                        '& .MuiToggleButton-root': {
                                            fontSize: '13px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            backgroundColor: '#f8f9fa',
                                            color: '#666',
                                            border: '1px solid #e0e0e0',
                                            '&:hover': {
                                                backgroundColor: '#f0f0f0',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#2B6BFD',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: '#1e5ae5',
                                                },
                                            },
                                            '&:first-of-type': {
                                                borderTopLeftRadius: '8px',
                                                borderBottomLeftRadius: '8px',
                                            },
                                            '&:last-of-type': {
                                                borderTopRightRadius: '8px',
                                                borderBottomRightRadius: '8px',
                                            },
                                        },
                                        '& .MuiToggleButtonGroup-grouped': {
                                            '&:not(:last-of-type)': {
                                                borderRight: 0,
                                            },
                                        },
                                    }}
                                >
                                    <ToggleButton value="Mp3">MP3</ToggleButton>
                                    <ToggleButton value="WAV">WAV</ToggleButton>
                                    <ToggleButton value="AAC">AAC</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {/* Export Button */}
                            <Button
                                variant="contained"
                                onClick={handleAudioExport}
                                fullWidth
                                sx={{
                                    mt: 1,
                                    height: '40px',
                                    textTransform: 'none',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    backgroundColor: '#2B6BFD',
                                    borderRadius: '8px',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#1e5ae5',
                                        boxShadow: 'none',
                                    }
                                }}
                            >
                                Export
                            </Button>
                        </Box>
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Transcript Name Input */}
                            <TextField
                                fullWidth
                                value={transcriptName}
                                onChange={(e) => setTranscriptName(e.target.value)}
                                placeholder="Enter filename"
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <TextSnippetIcon sx={{ color: '#999', fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        fontSize: '14px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '8px',
                                        '& fieldset': {
                                            borderColor: 'transparent',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#e0e0e0',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#2B6BFD',
                                            borderWidth: '1px',
                                        }
                                    }
                                }}
                            />

                            {/* Transcript Format Options */}
                            <Box>
                                <Typography 
                                    variant="body2" 
                                    sx={{ mb: 0.5, fontWeight: 500, fontSize: '13px' }}
                                >
                                    Format
                                </Typography>
                                <ToggleButtonGroup
                                    value={transcriptFormat}
                                    exclusive
                                    onChange={(event, newFormat) => {
                                        if (newFormat !== null) {
                                            setTranscriptFormat(newFormat);
                                        }
                                    }}
                                    aria-label="transcript format"
                                    size="small"
                                    fullWidth
                                    sx={{
                                        '& .MuiToggleButton-root': {
                                            fontSize: '13px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            backgroundColor: '#f8f9fa',
                                            color: '#666',
                                            border: '1px solid #e0e0e0',
                                            '&:hover': {
                                                backgroundColor: '#f0f0f0',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#2B6BFD',
                                                color: 'white',
                                                '&:hover': {
                                                    backgroundColor: '#1e5ae5',
                                                },
                                            },
                                            '&:first-of-type': {
                                                borderTopLeftRadius: '8px',
                                                borderBottomLeftRadius: '8px',
                                            },
                                            '&:last-of-type': {
                                                borderTopRightRadius: '8px',
                                                borderBottomRightRadius: '8px',
                                            },
                                        },
                                        '& .MuiToggleButtonGroup-grouped': {
                                            '&:not(:last-of-type)': {
                                                borderRight: 0,
                                            },
                                        },
                                    }}
                                >
                                    <ToggleButton value="SRT">SRT</ToggleButton>
                                    <ToggleButton value="VTT">VTT</ToggleButton>
                                    <ToggleButton value="TXT">TXT</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {/* Export Button */}
                            <Button
                                variant="contained"
                                onClick={handleTranscriptExport}
                                fullWidth
                                sx={{
                                    mt: 1,
                                    height: '40px',
                                    textTransform: 'none',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    backgroundColor: '#2B6BFD',
                                    borderRadius: '8px',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: '#1e5ae5',
                                        boxShadow: 'none',
                                    }
                                }}
                            >
                                Export
                            </Button>
                        </Box>
                    </TabPanel>
                </Box>
            </Card>
        </ClickAwayListener>
    );
}; 